import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * The one side drawer for the whole app (design: components/Drawer.tsx).
 * Right edge, slides in 200ms, leaves in 150ms. Esc and the overlay close it.
 * With `dirty`, closing asks to discard first, inline in the footer.
 *
 * Layout: header (eyebrow context, title, close) · scrolling body · sticky footer.
 * Footer: `danger` sits left (delete), `actions` right (Cancel is built in).
 */
export function Drawer({ open, onClose, title, context, width = "md", dirty = false, danger, actions, children, labelledBy = "drawer-title", closeLabel = "Cancel" }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const panel = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    if (open) {
      returnFocus.current = document.activeElement;
      setMounted(true);
      setConfirming(false);
      const f = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(f);
    }
    setShown(false);
    const t = window.setTimeout(() => {
      setMounted(false);
      returnFocus.current?.focus?.();
    }, 160);
    return () => window.clearTimeout(t);
  }, [open]);

  // Focus the first field once the panel has landed
  useEffect(() => {
    if (!shown) return undefined;
    const t = window.setTimeout(() => {
      const first = panel.current?.querySelector("[data-autofocus], input:not([type=hidden]), select, textarea");
      first?.focus();
    }, 210);
    return () => window.clearTimeout(t);
  }, [shown]);

  const requestClose = () => {
    if (dirty && !confirming) setConfirming(true);
    else onClose();
  };

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!mounted) return null;
  return createPortal(
    // `olli`: portalled to <body>, outside the frame, so it carries the design scope itself.
    <div className="olli fixed inset-0 z-[60] flex justify-end font-sans text-ink">
      <button type="button" aria-label="Close" tabIndex={-1} onClick={requestClose} className={`absolute inset-0 bg-ink/30 transition-opacity ${shown ? "opacity-100 duration-200" : "opacity-0 duration-150"}`} />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`relative m-3 flex max-w-[calc(100%-24px)] flex-col overflow-hidden rounded-xl bg-paper shadow-lift ring-1 ring-line transition-[transform,opacity] ease-out motion-reduce:transition-opacity ${width === "lg" ? "w-[560px]" : "w-[480px]"} ${shown ? "translate-x-0 opacity-100 duration-200" : "translate-x-8 opacity-0 duration-150"}`}
      >
        <header className="flex items-start justify-between gap-4 px-7 pb-5 pt-6">
          <div className="min-w-0">
            {context && <div className="mb-1.5 truncate text-caption text-muted">{context}</div>}
            <h2 id={labelledBy} className="text-h3 font-semibold tracking-tight">{title}</h2>
          </div>
          <button type="button" aria-label="Close" onClick={requestClose} className="-mr-2 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink">
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-7 pb-6">{children}</div>
        <footer className="flex min-h-[72px] items-center gap-2 border-t border-line px-7 py-4">
          {confirming ? (
            <div role="alert" className="flex w-full animate-fade-in items-center gap-2">
              <p className="mr-auto text-small font-medium">Discard your changes?</p>
              <button type="button" onClick={() => setConfirming(false)} className="h-10 rounded-full px-4 text-small font-semibold hover:bg-mist">Keep editing</button>
              <button type="button" onClick={onClose} className="h-10 rounded-full bg-ink px-4 text-small font-semibold text-paper hover:bg-ink/85">Discard</button>
            </div>
          ) : (
            <>
              <div className="mr-auto">{danger}</div>
              <button type="button" onClick={requestClose} className="h-11 rounded-full px-5 text-small font-semibold hover:bg-mist">{closeLabel}</button>
              {actions}
            </>
          )}
        </footer>
      </aside>
    </div>,
    document.body,
  );
}

/** A labelled group inside a drawer body. Hairline above every group after the first. */
export function DrawerGroup({ title, hint, children }) {
  return (
    <section className="flex flex-col gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0 [&+&]:mt-5">
      {title && (
        <div>
          <h3 className="text-small font-semibold">{title}</h3>
          {hint && <p className="mt-0.5 text-caption text-muted">{hint}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Field label + control + error/hint. Errors replace the hint. */
export function DrawerField({ label, htmlFor, optional, error, hint, children }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-caption font-semibold">
        {label}
        {optional && <span className="font-normal text-muted"> (optional)</span>}
      </label>
      {children}
      {error ? <p role="alert" className="animate-fade-in text-caption text-danger">{error}</p> : hint && <p className="text-caption text-muted">{hint}</p>}
    </div>
  );
}

/** Segmented choice (type, priority, status). Selected = ink pill. */
export function ChoiceRow({ value, options, onChange, label, render, selectedClass }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const sel = o === value;
        return (
          <button key={o} type="button" role="radio" aria-checked={sel} onClick={() => onChange(o)} className={`flex h-9 items-center gap-1.5 rounded-full px-3.5 text-small font-medium ${sel ? `${selectedClass?.(o) || "bg-ink"} text-paper` : "bg-mist text-ink hover:bg-line"}`}>
            {render ? render(o, sel) : o}
          </button>
        );
      })}
    </div>
  );
}

/** Shared control look for drawer inputs and selects. */
export const drawerInput =
  "h-11 w-full rounded-sm border border-line bg-paper px-3.5 text-small text-ink outline-none placeholder:text-muted/70 hover:border-muted/50 focus:border-ink focus:ring-2 focus:ring-ink/10 aria-[invalid=true]:border-danger";
